import { Link } from 'gatsby'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useOnClickOutside } from '../../hooks/useOnClickOutside'
import { styles } from './styles'
import Anchor from '../anchor'

export interface HeaderPopoverItem {
  key: string
  label: string
  to?: string
  external?: boolean
  /** Used for Sign In / Sign Out / Refer a friend actions */
  action?: 'signIn' | 'signOut' | 'referFriend'
}

interface HeaderPopoverProps {
  trigger: React.ReactNode
  items: HeaderPopoverItem[]
  align?: 'left' | 'right'
  /** Called when a nav item or footer action is chosen (before optional navigation). */
  onSelect?: () => void
  onSignIn?: () => void
  onSignOut?: () => void
  onReferFriend?: () => void
  menuLabel: string
}

const HeaderPopover = ({
  trigger,
  items,
  align = 'left',
  onSelect,
  onSignIn,
  onSignOut,
  onReferFriend,
  menuLabel,
}: HeaderPopoverProps) => {
  const classes = styles()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelIdRef = useRef<string>()
  if (!panelIdRef.current) {
    panelIdRef.current = `header-popover-${Math.random()
      .toString(36)
      .slice(2)}`
  }
  const panelId = panelIdRef.current

  const close = useCallback(() => setOpen(false), [])

  useOnClickOutside(rootRef, close, open)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        close()
        btnRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, close])

  const handleItem = (item: HeaderPopoverItem) => {
    if (item.action === 'signIn') {
      onSignIn?.()
      close()
      return
    }
    if (item.action === 'signOut') {
      onSignOut?.()
      close()
      return
    }
    if (item.action === 'referFriend') {
      onReferFriend?.()
      close()
      return
    }
    onSelect?.()
    close()
  }

  const renderLink = (item: HeaderPopoverItem) => {
    const className = classes.headerPopoverLink
    if (item.external && item.to) {
      return (
        <Anchor
          target="_blank"
          key={item.key}
          href={item.to}
          role="menuitem"
          className={className}
          onClick={() => handleItem(item)}
        >
          {item.label}
        </Anchor>
      )
    }
    if (item.to) {
      return (
        <Link
          key={item.key}
          to={item.to}
          role="menuitem"
          className={className}
          onClick={() => handleItem(item)}
        >
          {item.label}
        </Link>
      )
    }
    return (
      <button
        key={item.key}
        type="button"
        role="menuitem"
        className={classes.headerPopoverButton}
        onClick={() => handleItem(item)}
      >
        {item.label}
      </button>
    )
  }

  return (
    <div ref={rootRef} className={classes.headerPopoverRoot}>
      <button
        ref={btnRef}
        type="button"
        className={classes.headerPopoverTrigger}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls={panelId}
        aria-label={menuLabel}
        onClick={() => setOpen(o => !o)}
      >
        {trigger}
      </button>
      {open && (
        <div
          id={panelId}
          role="menu"
          aria-label={menuLabel}
          className={`${classes.headerPopoverPanel} ${
            align === 'right'
              ? classes.headerPopoverPanelRight
              : classes.headerPopoverPanelLeft
          }`}
        >
          {items.map(renderLink)}
        </div>
      )}
    </div>
  )
}

export default HeaderPopover
